import React, { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  Backdrop,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { ActionButton } from "../../components/shared/action-button";
import {
  MainContainer,
  LoginPanel,
  Content,
  HeroImage,
  FormContainer,
  Logo,
  Form,
  Footer,
  ForgotPasswordLink,
  ForgotPasswordText,
  LogoContainer,
  Title,
} from "./Login.styles";
import api from "../../api/config";
import { UpdatePassword } from "../../api/Authentication";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Notification from "../../components/shared/Notification";
import mammo_login_image from "../../assets/images/login_viewer_list1.png";
import nexus_logo from "../../assets/images/MOVU_logo_white.png";
import { StyledTextFieldGroup } from "../../components/shared/StyledTextFieldGroup";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const ResetPassword = ({ showNotification }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Password validation states
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [isSpecialValid, setIsSpecialValid] = useState(false);
  const [isNumberValid, setIsNumberValid] = useState(false);

  // Password validation function
  const validatePassword = (password) => {
    setIsLengthValid(password.length >= 8);
    setIsSpecialValid(/[!@#$%^&*(),.?":{}|<>]/.test(password));
    setIsNumberValid(/\d/.test(password));
  };

  // Update password validation when password changes
  useEffect(() => {
    if (password) {
      validatePassword(password);
    } else {
      setIsLengthValid(false);
      setIsSpecialValid(false);
      setIsNumberValid(false);
    }
  }, [password]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token) {
      localStorage.setItem("token", token);
    } else {
      setNotification({
        open: true,
        message: "Token not found in URL",
        severity: "error",
      });
    }
  }, []);

  const checkPasswords = () => {
    if (password !== confirmPassword) {
      setNotification({
        open: true,
        message: "Passwords do not match",
        severity: "error",
      });
      return false;
    }
    return true;
  };

  const redirectToLogin = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!checkPasswords()) {
      setLoading(false);
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");

    if (!token) {
      setNotification({
        open: true,
        message: "Invalid or missing reset token",
        severity: "error",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await api.put(
        `${UpdatePassword}?login_location=dashboard`,
        { password },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Reset password response:", response);

      const message = response.data.status || "Unexpected response";
      setNotification({
        open: true,
        message,
        severity: "success",
      });
      localStorage.removeItem("token"); 
      redirectToLogin();
    } catch (error) {
      console.error("Reset password error:", error);
      const errorMessage =
        error.response?.data?.detail || "Failed to send reset link";
      setNotification({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer>
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        handleClose={() => setNotification({ ...notification, open: false })}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <LoginPanel>
        <Content>
          <HeroImage src={mammo_login_image} alt="Hero" />
          <FormContainer>
            <LogoContainer>
              <Logo src={nexus_logo} alt="Logo" />
            </LogoContainer>
            <Form onSubmit={handleResetPassword}>
              <Title variant="b">
                Reset Password
              </Title>
              <Typography
                variant="body2"
                style={{ marginBottom: "20px", color: "#F8F8F8" }}
              >
                Please enter your new password. Make sure your new password is different from the previous one.
              </Typography>
              <StyledTextFieldGroup
                label="New Password"
                placeholder="New Password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                        edge="end"
                        sx={{ color: "#A5E0FF" }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: Boolean(password) || undefined,
                }}
                error={password && !(isLengthValid && isSpecialValid && isNumberValid)}
              />
              
              {password && (
                <div style={{ 
                  marginBottom: "24px",
                  fontWeight: "400",
                  fontSize: "12px",
                  lineHeight: "16px"
                }}>
                  <Typography variant="body2">
                    Your password must meet the following criteria:
                  </Typography>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Typography 
                      variant="body2" 
                      style={{ 
                        color: isLengthValid ? "#99FFC8" : "#F8F8F8",
                        fontWeight: isLengthValid ? "700" : "400",
                      }}
                    >
                      • Contain at least 8 characters
                    </Typography>
                    <Typography 
                      variant="body2" 
                      style={{ color: isSpecialValid ? "#99FFC8" : "#F8F8F8",
                        fontWeight: isSpecialValid ? "700" : "400",
                      }}
                    >
                      • Contain at least 1 special character (ex. !@#$%)
                    </Typography>
                    <Typography 
                      variant="body2" 
                      style={{ color: isNumberValid ? "#99FFC8" : "#F8F8F8",
                        fontWeight: isNumberValid ? "700" : "400",
                      }}
                    >
                      • Contain at least 1 number
                    </Typography>
                  </div>
                </div>
              )}
              
              <StyledTextFieldGroup
                label="Confirm Password"
                placeholder="Confirm Password"
                variant="outlined"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                        edge="end"
                        sx={{ color: "#A5E0FF" }}
                      >
                        {showConfirmPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: Boolean(password) || undefined,
                }}
                error={confirmPassword && password !== confirmPassword}
                helperText={confirmPassword && password !== confirmPassword ? "Passwords do not match" : ""}
              />
              <div style={{ marginTop: "36px" }}>
                <ActionButton type="submit" sx={{ height: "50px" }} fullWidth disabled={!(isLengthValid && isSpecialValid && isNumberValid) || password !== confirmPassword}>
                  Reset Password
                </ActionButton>
              </div>
              <ForgotPasswordText variant="body2" style={{ marginTop: "36px" }}>
                <ForgotPasswordLink
                  onClick={redirectToLogin}
                  style={{ cursor: "pointer" }}
                >
                  <div style={{ display: "flex", justifyContent: "left" }}>
                    <ChevronLeftIcon></ChevronLeftIcon>
                    <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                      Back to Login
                    </div>
                  </div>
                </ForgotPasswordLink>
              </ForgotPasswordText>
            </Form>
            <Footer>
              <Typography
                variant="body2"
                style={{ color: "#9DA2A7", marginTop: "30px" }}
              >
                Powered by Nexus-MD
              </Typography>
            </Footer>
          </FormContainer>
        </Content>
      </LoginPanel>
    </MainContainer>
  );
};

export default ResetPassword;
